import { useCallback, useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import type { ProductAislesPreference, ProductAislesPreferenceQueryListArgs, Query } from "@/graphql/codegen/graphql"
import { GetProductAislePreferenceListDocument } from "@/graphql/codegen/graphql"

interface UseGetProductAislesPreferencesReturn {
  aislesPreferences: ProductAislesPreference[]
  isFetching: boolean
  error?: CombinedError
  refetch: () => void
}

const LIMIT = 100

export const useGetProductAislesPreferences = (productId: number): UseGetProductAislesPreferencesReturn => {
  const [aislesPreferences, setAislesPreferences] = useState<ProductAislesPreference[]>([])
  const [offset, setOffset] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [{ data, fetching, error }] = useQuery<
    Pick<Query, "productAislesPreference">,
    ProductAislesPreferenceQueryListArgs
  >({
    query: GetProductAislePreferenceListDocument,
    variables: { input: { filter: { productId }, limit: LIMIT, offset } },
  })

  useEffect(() => {
    if (data?.productAislesPreference.list.__typename === "ProductAislesPreferenceListSuccess") {
      const paginatedProductAislePreferences = data.productAislesPreference.list.productAislesPreferences
      setAislesPreferences((prev: ProductAislesPreference[]) =>
        offset === 0 ? paginatedProductAislePreferences : [...prev, ...paginatedProductAislePreferences]
      )

      if (paginatedProductAislePreferences.length < LIMIT) {
        setHasNextPage(false)
      }

      loadMore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const loadMore = () => {
    if (hasNextPage) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
  }

  const refetch = useCallback(() => {
    setOffset(0)
    setAislesPreferences([])
    setHasNextPage(true)
  }, [setOffset, setAislesPreferences, setHasNextPage])

  return {
    aislesPreferences,
    isFetching: fetching,
    error,
    refetch,
  }
}
